/**
 * Gestión de Cobros — cartera asignada, cola de promesas y efectividad.
 *
 * Complementa la pestaña Gestión del expediente (donde se asigna el gestor y
 * se registran las notas): aquí el supervisor/gestor ve la cartera completa.
 *
 *  - Cartera asignada: los financiamientos que tiene cada gestor, con su
 *    balance, atraso, última gestión y promesas abiertas. El filtro "Gestor"
 *    responde la pregunta de siempre: qué préstamos lleva fulano.
 *  - Promesas de pago: las abiertas de todos los préstamos (vencidas
 *    primero, resaltadas). El cobro las cierra solo (backend marca
 *    fechacumplida al guardar el pago; cancelar el recibo las reabre).
 *  - Efectividad por gestor: en un rango de fechas, cartera asignada,
 *    gestiones registradas, promesas hechas/cumplidas y monto recuperado.
 *
 * Clic en una fila (de cualquier tabla de préstamos) abre el expediente; clic
 * en una fila de la efectividad filtra la cartera por ese gestor.
 */
import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Chip from "@mui/material/Chip";
import { DataGrid } from "@mui/x-data-grid";
import FolderSharedOutlinedIcon from "@mui/icons-material/FolderSharedOutlined";
import EventBusyOutlinedIcon from "@mui/icons-material/EventBusyOutlined";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import GavelRoundedIcon from "@mui/icons-material/GavelRounded";
import { useGlobalMDAlert, useMaterialUIController } from "context";
import { tienePermisosModulo } from "lib/seguridad/permisosPorModulo";
import { FP, PageHeader, fpInputSx, fpDataGridSx } from "components/financepro";
import { useApi } from "lib/api";
import { estadoPrestamo } from "lib/prestamos";

const fmtMoney = (v) => {
  const n = typeof v === "number" ? v : parseFloat(String(v || "").replace(/,/g, ""));
  if (Number.isNaN(n)) return "0.00";
  return n.toLocaleString("es-DO", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const money = (value) => fmtMoney(value);

const sectionBox = {
  border: `1px solid ${FP.color.border}`,
  borderRadius: 2.5,
  p: { xs: 2, sm: 2.6 },
  mb: 2.4,
  bgcolor: FP.color.cardBg,
  boxShadow: FP.shadow.soft,
};

const tituloSeccion = {
  fontWeight: 800,
  fontSize: "0.98rem",
  color: FP.color.textPrimary,
  mb: 0.4,
};

const subtituloSeccion = { fontSize: "0.82rem", color: FP.color.textSecondary, mb: 2 };

const hoyISO = () => new Date().toISOString().slice(0, 10);
const inicioMesISO = () => `${new Date().toISOString().slice(0, 8)}01`;

function ChipPromesa({ estado }) {
  if (estado === "cumplida")
    return (
      <Chip
        size="small"
        label="Cumplida"
        sx={{ bgcolor: "#E8F5E9", color: "#2E7D32", fontWeight: 700 }}
      />
    );
  if (estado === "vencida")
    return (
      <Chip
        size="small"
        label="Vencida"
        sx={{ bgcolor: FP.color.dangerSoft, color: FP.color.danger, fontWeight: 700 }}
      />
    );
  return (
    <Chip
      size="small"
      label="Pendiente"
      sx={{ bgcolor: "#FFF7E6", color: "#B45309", fontWeight: 700 }}
    />
  );
}

function ChipEstado({ estado }) {
  if (estado === "activo")
    return (
      <Chip
        size="small"
        label="Activo"
        sx={{ bgcolor: "#E8F5E9", color: "#2E7D32", fontWeight: 700 }}
      />
    );
  if (estado === "saldado")
    return (
      <Chip
        size="small"
        label="Saldado"
        sx={{ bgcolor: "#E3F2FD", color: "#1565C0", fontWeight: 700 }}
      />
    );
  if (estado === "cancelado")
    return (
      <Chip
        size="small"
        label="Cancelado"
        sx={{ bgcolor: FP.color.dangerSoft, color: FP.color.danger, fontWeight: 700 }}
      />
    );
  return <span>—</span>;
}

export default function GestionCobros() {
  const { showAlert } = useGlobalMDAlert();
  const { post } = useApi();
  const navigate = useNavigate();
  const [controller] = useMaterialUIController();
  const { roleDetalles } = controller;
  const puedeSolicitarIncauto =
    tienePermisosModulo(roleDetalles, "ares5030") || tienePermisosModulo(roleDetalles, "ares5010");
  // Préstamos de la cartera marcados para una solicitud de incauto.
  const [marcados, setMarcados] = useState([]);

  // ---- Cartera asignada ----
  // Se trae completa una sola vez y se filtra en memoria: son las
  // asignaciones vigentes del tenant (cientos de filas), y así el selector
  // conoce a todos los gestores sin una consulta aparte.
  const [cartera, setCartera] = useState([]);
  const [cargandoCartera, setCargandoCartera] = useState(false);
  const [gestorFiltro, setGestorFiltro] = useState("todos");
  const [estadoCartera, setEstadoCartera] = useState("activos");
  const [busquedaCartera, setBusquedaCartera] = useState("");
  const refCartera = useRef(null);

  useEffect(() => {
    let activo = true;
    setCargandoCartera(true);
    post("/api/gestioncobros_cartera", {})
      .then(({ ok, data }) => {
        if (!activo) return;
        if (ok && Array.isArray(data.cartera)) setCartera(data.cartera);
        else
          showAlert({
            message: data.Error || "No se pudo cargar la cartera asignada",
            color: "error",
          });
      })
      .finally(() => activo && setCargandoCartera(false));
    return () => {
      activo = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const gestoresCartera = useMemo(
    () => [...new Set(cartera.map((c) => c.gestor).filter(Boolean))].sort(),
    [cartera]
  );

  const carteraFiltrada = useMemo(() => {
    const q = busquedaCartera.trim().toLowerCase();
    return cartera.filter((c) => {
      if (gestorFiltro !== "todos" && c.gestor !== gestorFiltro) return false;
      if (estadoCartera === "activos" && estadoPrestamo(c.status, c.balance) !== "activo")
        return false;
      if (!q) return true;
      return [c.noprest, c.cliente, c.cedula, c.marca, c.modelo, c.gestor]
        .join(" ")
        .toLowerCase()
        .includes(q);
    });
  }, [cartera, gestorFiltro, estadoCartera, busquedaCartera]);

  /** Clic en la efectividad: mostrar los préstamos de ese gestor. */
  const verCarteraDe = (gestor) => {
    setGestorFiltro(gestor);
    setEstadoCartera("todos");
    refCartera.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  // ---- Promesas ----
  const [promesas, setPromesas] = useState([]);
  const [estadoPromesas, setEstadoPromesas] = useState("abiertas");
  const [cargandoPromesas, setCargandoPromesas] = useState(false);

  useEffect(() => {
    let activo = true;
    setCargandoPromesas(true);
    post("/api/gestioncobros_promesas", { estado: estadoPromesas })
      .then(({ ok, data }) => {
        if (!activo) return;
        if (ok && Array.isArray(data.promesas)) setPromesas(data.promesas);
        else
          showAlert({ message: data.Error || "No se pudieron cargar las promesas", color: "error" });
      })
      .finally(() => activo && setCargandoPromesas(false));
    return () => {
      activo = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [estadoPromesas]);

  // ---- Efectividad ----
  const [desde, setDesde] = useState(inicioMesISO());
  const [hasta, setHasta] = useState(hoyISO());
  const [gestores, setGestores] = useState([]);
  const [cargandoEfectividad, setCargandoEfectividad] = useState(false);

  const cargarEfectividad = useCallback(() => {
    setCargandoEfectividad(true);
    post("/api/gestioncobros_efectividad", { desde, hasta })
      .then(({ ok, data }) => {
        if (ok && Array.isArray(data.gestores)) setGestores(data.gestores);
        else
          showAlert({
            message: data.Error || "No se pudo calcular la efectividad",
            color: "error",
          });
      })
      .finally(() => setCargandoEfectividad(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [desde, hasta]);

  useEffect(() => {
    cargarEfectividad();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const vencidas = promesas.filter((p) => p.estadopromesa === "vencida").length;

  const columnasCartera = [
    { field: "noprest", headerName: "Préstamo", width: 100 },
    { field: "cliente", headerName: "Cliente", flex: 1, minWidth: 180 },
    {
      field: "telefono",
      headerName: "Teléfono",
      width: 150,
      valueGetter: (_v, row) => [row.telefono, row.celular].filter(Boolean).join(" / "),
    },
    {
      field: "vehiculo",
      headerName: "Vehículo",
      width: 160,
      valueGetter: (_v, row) => [row.marca, row.modelo].filter(Boolean).join(" "),
    },
    { field: "gestor", headerName: "Gestor", width: 120 },
    { field: "fechaasignacion", headerName: "Asignado el", width: 150 },
    {
      field: "balance",
      headerName: "Balance",
      width: 120,
      align: "right",
      headerAlign: "right",
      valueFormatter: money,
    },
    {
      field: "cuotasatrasadas",
      headerName: "Atraso",
      width: 90,
      align: "center",
      headerAlign: "center",
      renderCell: (p) =>
        p.value > 0 ? (
          <Chip
            size="small"
            label={`${p.value} ${p.value === 1 ? "cuota" : "cuotas"}`}
            sx={{ bgcolor: FP.color.dangerSoft, color: FP.color.danger, fontWeight: 700 }}
          />
        ) : (
          "—"
        ),
    },
    {
      field: "promesasabiertas",
      headerName: "Promesas",
      width: 100,
      align: "center",
      headerAlign: "center",
      valueGetter: (value) => value || 0,
      renderCell: (p) => (p.value > 0 ? p.value : "—"),
    },
    {
      field: "ultimagestion",
      headerName: "Última gestión",
      width: 160,
      valueGetter: (value) => value || "",
      renderCell: (p) => p.value || "Sin gestiones",
    },
    {
      field: "estado",
      headerName: "Estado",
      width: 110,
      valueGetter: (_v, row) => estadoPrestamo(row.status, row.balance) || "",
      renderCell: (p) => <ChipEstado estado={p.value} />,
    },
  ];

  const columnasPromesas = [
    { field: "noprest", headerName: "Préstamo", width: 100 },
    { field: "cliente", headerName: "Cliente", flex: 1, minWidth: 180 },
    {
      field: "telefono",
      headerName: "Teléfono",
      width: 150,
      valueGetter: (_v, row) => [row.telefono, row.celular].filter(Boolean).join(" / "),
    },
    { field: "gestor", headerName: "Gestor", width: 120 },
    { field: "usuario", headerName: "Registrada por", width: 130 },
    { field: "fechapromesa", headerName: "Prometido para", width: 130 },
    {
      field: "montopromesa",
      headerName: "Monto",
      width: 120,
      align: "right",
      headerAlign: "right",
      valueFormatter: money,
    },
    {
      field: "estadopromesa",
      headerName: "Estado",
      width: 120,
      renderCell: (p) => <ChipPromesa estado={p.value} />,
    },
    { field: "fechacumplida", headerName: "Cumplida el", width: 150 },
  ];

  const columnasGestores = [
    { field: "gestor", headerName: "Gestor", flex: 1, minWidth: 140 },
    {
      field: "prestamos",
      headerName: "Cartera",
      width: 100,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "gestiones",
      headerName: "Gestiones",
      width: 110,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "promesas",
      headerName: "Promesas",
      width: 110,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "cumplidas",
      headerName: "Cumplidas",
      width: 110,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "cumplimiento",
      headerName: "% Cumpl.",
      width: 100,
      align: "center",
      headerAlign: "center",
      valueGetter: (_v, row) =>
        row.promesas > 0 ? `${Math.round((row.cumplidas / row.promesas) * 100)}%` : "—",
    },
    {
      field: "montoprometido",
      headerName: "Prometido",
      width: 130,
      align: "right",
      headerAlign: "right",
      valueFormatter: money,
    },
    {
      field: "recuperado",
      headerName: "Recuperado",
      width: 130,
      align: "right",
      headerAlign: "right",
      valueFormatter: money,
    },
  ];

  return (
    <Box component="main" sx={{ minHeight: "100vh", bgcolor: FP.color.surface }}>
      <PageHeader
        title="Gestión de Cobros"
        subtitle="Financiamientos asignados, promesas de pago y efectividad por gestor"
      />
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        {/* Cartera asignada */}
        <Box sx={sectionBox} ref={refCartera}>
          <Stack direction="row" alignItems="center" spacing={1.2} sx={{ mb: 0.4 }}>
            <FolderSharedOutlinedIcon sx={{ fontSize: 20, color: FP.color.navy }} />
            <Typography sx={{ ...tituloSeccion, mb: 0 }}>Cartera asignada</Typography>
            <Chip
              size="small"
              label={`${carteraFiltrada.length} ${
                carteraFiltrada.length === 1 ? "financiamiento" : "financiamientos"
              }`}
              sx={{ bgcolor: FP.color.navySoft, color: "#FFFFFF", fontWeight: 700 }}
            />
          </Stack>
          <Typography sx={subtituloSeccion}>
            Financiamientos con gestor asignado. Filtra por gestor para ver su cartera; haz clic en
            una fila para abrir el expediente.
          </Typography>
          <Grid container spacing={2} sx={{ mb: 1.5 }}>
            <Grid item xs={12} sm={4} md={3}>
              <TextField
                fullWidth
                label="Buscar"
                placeholder="Préstamo, cliente, cédula o vehículo"
                value={busquedaCartera}
                onChange={(e) => setBusquedaCartera(e.target.value)}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={12} sm={4} md={2}>
              <TextField
                select
                fullWidth
                label="Gestor"
                value={gestorFiltro}
                onChange={(e) => setGestorFiltro(e.target.value)}
                sx={fpInputSx}
              >
                <MenuItem value="todos">Todos</MenuItem>
                {gestoresCartera.map((g) => (
                  <MenuItem key={g} value={g}>
                    {g}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={4} md={2}>
              <TextField
                select
                fullWidth
                label="Estado"
                value={estadoCartera}
                onChange={(e) => setEstadoCartera(e.target.value)}
                sx={fpInputSx}
              >
                <MenuItem value="activos">Activos</MenuItem>
                <MenuItem value="todos">Todos</MenuItem>
              </TextField>
            </Grid>
            {puedeSolicitarIncauto && (
              <Grid item xs={12} md sx={{ display: "flex", justifyContent: { md: "flex-end" }, alignItems: "center" }}>
                <Button
                  variant="outlined"
                  disabled={!marcados.length}
                  startIcon={<GavelRoundedIcon />}
                  onClick={() =>
                    navigate("/incautos/solicitudes", { state: { noprests: marcados.map(String) } })
                  }
                  sx={{
                    borderColor: "#B42318",
                    color: "#B42318",
                    textTransform: "none",
                    fontWeight: 700,
                    "&:hover": { bgcolor: "#FEF3F2", borderColor: "#B42318" },
                  }}
                >
                  {marcados.length
                    ? `Solicitar incauto (${marcados.length})`
                    : "Marque préstamos para solicitar incauto"}
                </Button>
              </Grid>
            )}
          </Grid>
          <Box sx={{ bgcolor: "#fff" }}>
            <DataGrid
              autoHeight
              rows={carteraFiltrada}
              getRowId={(r) => r.noprest}
              columns={columnasCartera}
              loading={cargandoCartera}
              rowHeight={44}
              hideFooter={carteraFiltrada.length <= 25}
              pageSizeOptions={[25, 50, 100]}
              initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
              sx={{ ...fpDataGridSx, "--DataGrid-overlayHeight": "160px", cursor: "pointer" }}
              disableRowSelectionOnClick
              checkboxSelection={puedeSolicitarIncauto}
              keepNonExistentRowsSelected
              rowSelectionModel={marcados}
              onRowSelectionModelChange={setMarcados}
              // Clic en la casilla marca; en el resto de la fila abre el expediente.
              onCellClick={(params) => {
                if (params.field === "__check__") return;
                navigate(`/prestamos/expediente/${params.row.noprest}`);
              }}
              localeText={{ noRowsLabel: "No hay financiamientos asignados que mostrar" }}
            />
          </Box>
        </Box>

        {/* Promesas */}
        <Box sx={sectionBox}>
          <Stack direction="row" alignItems="center" spacing={1.2} sx={{ mb: 0.4 }}>
            <EventBusyOutlinedIcon sx={{ fontSize: 20, color: FP.color.navy }} />
            <Typography sx={{ ...tituloSeccion, mb: 0 }}>Promesas de pago</Typography>
            {vencidas > 0 && (
              <Chip
                size="small"
                label={`${vencidas} vencidas`}
                sx={{ bgcolor: FP.color.dangerSoft, color: FP.color.danger, fontWeight: 700 }}
              />
            )}
          </Stack>
          <Typography sx={subtituloSeccion}>
            El cobro del préstamo cierra la promesa automáticamente. Haz clic en una fila para
            abrir el expediente.
          </Typography>
          <Grid container spacing={2} sx={{ mb: 1.5 }}>
            <Grid item xs={12} sm={4} md={2}>
              <TextField
                select
                fullWidth
                label="Mostrar"
                value={estadoPromesas}
                onChange={(e) => setEstadoPromesas(e.target.value)}
                sx={fpInputSx}
              >
                <MenuItem value="abiertas">Abiertas</MenuItem>
                <MenuItem value="todas">Todas</MenuItem>
              </TextField>
            </Grid>
          </Grid>
          <Box sx={{ bgcolor: "#fff" }}>
            <DataGrid
              autoHeight
              rows={promesas}
              columns={columnasPromesas}
              loading={cargandoPromesas}
              rowHeight={44}
              hideFooter={promesas.length <= 25}
              pageSizeOptions={[25, 50, 100]}
              initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
              sx={{ ...fpDataGridSx, "--DataGrid-overlayHeight": "160px", cursor: "pointer" }}
              disableRowSelectionOnClick
              onRowClick={(params) => navigate(`/prestamos/expediente/${params.row.noprest}`)}
              localeText={{ noRowsLabel: "No hay promesas de pago que mostrar" }}
            />
          </Box>
        </Box>

        {/* Efectividad */}
        <Box sx={sectionBox}>
          <Stack direction="row" alignItems="center" spacing={1.2} sx={{ mb: 0.4 }}>
            <AssessmentOutlinedIcon sx={{ fontSize: 20, color: FP.color.navy }} />
            <Typography sx={{ ...tituloSeccion, mb: 0 }}>Efectividad por gestor</Typography>
          </Stack>
          <Typography sx={subtituloSeccion}>
            Gestiones y promesas registradas por cada usuario en el rango, y lo recuperado en los
            préstamos de su cartera vigente.
          </Typography>
          <Grid container spacing={2} sx={{ mb: 1.5 }}>
            <Grid item xs={6} sm={3} md={2}>
              <TextField
                fullWidth
                type="date"
                label="Desde"
                value={desde}
                onChange={(e) => setDesde(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <TextField
                fullWidth
                type="date"
                label="Hasta"
                value={hasta}
                onChange={(e) => setHasta(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={12} sm={3} md={2} sx={{ display: "flex", alignItems: "center" }}>
              <Button
                fullWidth
                onClick={cargarEfectividad}
                disabled={cargandoEfectividad}
                sx={{
                  textTransform: "none",
                  borderRadius: 1.8,
                  fontWeight: 700,
                  boxShadow: "none",
                  bgcolor: FP.color.navy,
                  color: "#FFFFFF",
                  height: 46,
                  "&:hover": { bgcolor: FP.color.navySoft, boxShadow: "none", color: "#FFFFFF" },
                  "&.Mui-disabled": { bgcolor: `${FP.color.navy}66`, color: "#FFFFFF" },
                }}
              >
                Calcular
              </Button>
            </Grid>
          </Grid>
          <Box sx={{ bgcolor: "#fff" }}>
            <DataGrid
              autoHeight
              rows={gestores.map((g) => ({ id: g.gestor, ...g }))}
              columns={columnasGestores}
              loading={cargandoEfectividad}
              rowHeight={44}
              hideFooter={gestores.length <= 25}
              pageSizeOptions={[25, 50]}
              initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
              sx={{ ...fpDataGridSx, "--DataGrid-overlayHeight": "160px", cursor: "pointer" }}
              disableRowSelectionOnClick
              onRowClick={(params) => verCarteraDe(params.row.gestor)}
              localeText={{ noRowsLabel: "Sin gestiones en el rango seleccionado" }}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
